/**
 * THE LIVE REGIONS — the one place this app says something out loud.
 *
 * Two regions, created once, never removed:
 *
 *   POLITE     `aria-live="polite"` `role="status"`. Queued behind whatever the
 *              screen reader is already saying. Everything that is information
 *              goes here: a screen changed, mana is full, a setting saved.
 *   ASSERTIVE  `aria-live="assertive"` `role="alert"`. Interrupts. Reserved for
 *              the two things a player cannot afford to hear late: a tower
 *              falling and the match ending.
 *
 * ── WHY A SINGLETON, WHEN SCREENS ALREADY HAVE THEIR OWN REGIONS ────────────
 * They do, and they should keep them: `meta/shell.ts` announces the route,
 * `meta/settings-screen.ts` announces "Saved", `meta/menu.ts` announces the
 * queue. Those are *scoped* messages that belong to a screen and die with it.
 *
 * This pair exists for messages whose SOURCE outlives the screen — the match
 * canvas, which has no DOM to hang a region on, and the settings applier, which
 * fires from a store subscription. A region created and destroyed with a screen
 * cannot carry those: a live region must be IN THE DOM BEFORE the text changes,
 * or the change is not a change and nothing is announced. That is the single
 * most common way an `aria-live` region ships broken, and creating the node and
 * setting its text in the same frame is exactly the shape that does it.
 * `ensureLiveRegions()` is therefore called at boot, not at announce time.
 *
 * ── WHY THE TEXT IS CLEARED FIRST ───────────────────────────────────────────
 * A screen reader announces a live region when its CONTENT CHANGES. Announcing
 * the same string twice — two towers falling, "Mana full" in two successive
 * matches — writes the identical text and changes nothing, so the second one is
 * silent. Clearing to `''` and writing on a later task makes it a real change.
 * `queueMicrotask` is not enough (the two mutations coalesce into one paint and
 * one accessibility-tree update); a macrotask is. `setTimeout(…, 0)` is the
 * portable one, and the pending id is tracked so a burst of announcements ends
 * with the LAST one spoken rather than with all of them racing.
 */

export const POLITE_REGION_ID = 'rga-live-polite';
export const ASSERTIVE_REGION_ID = 'rga-live-assertive';

/**
 * The visually-hidden recipe, inline.
 *
 * NOT `class="sr-only"`: these nodes are appended to `document.body` from the
 * shell, and a page that has not loaded the Tailwind bundle (a test harness, a
 * pre-hydration paint) would render two blocks of announcement text in the
 * middle of the layout. Inline styles cannot be missing.
 *
 * `display:none` and `visibility:hidden` are NOT usable here: both remove the
 * node from the accessibility tree, so the region would be silent. The clip
 * rectangle is the only technique that hides a node visually while leaving it
 * present to assistive technology.
 */
const HIDDEN_STYLE =
  'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;' +
  'clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0';

export interface LiveRegions {
  readonly polite: HTMLElement;
  readonly assertive: HTMLElement;
}

function makeRegion(doc: Document, id: string, assertive: boolean): HTMLElement {
  const node = doc.createElement('p');
  node.id = id;
  node.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
  node.setAttribute('role', assertive ? 'alert' : 'status');
  // Read the whole region on every change. Without it, a reader may announce
  // only the changed text node, which for a replaced string is the whole
  // message anyway — but for a partial update is a fragment.
  node.setAttribute('aria-atomic', 'true');
  node.setAttribute('data-testid', id);
  node.setAttribute('style', HIDDEN_STYLE);
  return node;
}

/**
 * Create the pair if it is not there, and return it. Idempotent.
 * Returns `null` before `<body>` exists — the caller simply does not announce.
 */
export function ensureLiveRegions(doc: Document): LiveRegions | null {
  const body = doc.body;
  if (body === null || body === undefined) return null;
  let polite = doc.getElementById(POLITE_REGION_ID);
  if (polite === null) {
    polite = makeRegion(doc, POLITE_REGION_ID, false);
    body.appendChild(polite);
  }
  let assertive = doc.getElementById(ASSERTIVE_REGION_ID);
  if (assertive === null) {
    assertive = makeRegion(doc, ASSERTIVE_REGION_ID, true);
    body.appendChild(assertive);
  }
  return { polite, assertive };
}

/** Pending re-write per region, so a burst does not fight itself. */
interface Pending {
  readonly timer: ReturnType<typeof setTimeout>;
  readonly text: string;
}
const pending = new WeakMap<HTMLElement, Pending>();

export interface AnnounceOptions {
  /** Interrupt rather than queue. Towers and results only. */
  readonly assertive?: boolean;
  readonly document?: Document;
}

/**
 * Say something. A blank or whitespace-only message is a no-op, because an
 * empty live region is a silence a caller almost never meant to request.
 *
 * Returns the region it wrote to, or `null` if there was nowhere to write.
 */
export function announce(message: string, options: AnnounceOptions = {}): HTMLElement | null {
  const text = message.trim();
  if (text === '') return null;
  const doc = options.document ?? (typeof document === 'undefined' ? null : document);
  if (doc === null) return null;
  const regions = ensureLiveRegions(doc);
  if (regions === null) return null;
  const region = options.assertive === true ? regions.assertive : regions.polite;

  const queued = pending.get(region);
  if (queued !== undefined) clearTimeout(queued.timer);
  region.textContent = '';
  const timer = setTimeout(() => {
    pending.delete(region);
    region.textContent = text;
  }, 0);
  pending.set(region, { timer, text });
  return region;
}

/**
 * Write any pending announcement NOW instead of on the next task.
 *
 * TESTS ONLY. It exists because a jsdom test should be able to assert the text
 * that will be spoken without owning a fake clock — `vi.useFakeTimers()` in one
 * suite changes the behaviour of every module that schedules anything, and this
 * module schedules on a path the whole product runs through.
 */
export function flushAnnouncements(doc?: Document): void {
  const d = doc ?? (typeof document === 'undefined' ? null : document);
  if (d === null) return;
  const regions = ensureLiveRegions(d);
  if (regions === null) return;
  for (const region of [regions.polite, regions.assertive]) {
    const queued = pending.get(region);
    if (queued === undefined) continue;
    clearTimeout(queued.timer);
    pending.delete(region);
    region.textContent = queued.text;
  }
}

/** Remove both regions and drop any pending write. Tests and teardown. */
export function resetLiveRegions(doc?: Document): void {
  const d = doc ?? (typeof document === 'undefined' ? null : document);
  if (d === null) return;
  for (const id of [POLITE_REGION_ID, ASSERTIVE_REGION_ID]) {
    const node = d.getElementById(id);
    if (node === null) continue;
    const queued = pending.get(node as HTMLElement);
    if (queued !== undefined) {
      clearTimeout(queued.timer);
      pending.delete(node as HTMLElement);
    }
    node.remove();
  }
}
